/**
 * Front-line visualization — influence map, contour extraction, smoothing, event detection.
 * Pure computation module — no Phaser dependency.
 */
import type { TurnWorld, PlayerId } from './types';
import { TERRAIN_DEFS } from './terrain';

// ── Types ────────────────────────────────────────────────────────

export interface InfluenceEntry {
  owner: PlayerId;
  cost: number;
}

/** Flat-array influence grid — avoids Map/string-key overhead. */
export interface InfluenceGrid {
  width: number;
  height: number;
  /** Owner per tile (row * width + col). -1 = unowned. */
  owner: Int16Array;
  /** Cost per tile (row * width + col). */
  cost: Float32Array;
}

export interface FrontLineSegment {
  /** Smoothed screen-space polyline. */
  points: { x: number; y: number }[];
  /** Player on one side of the boundary. */
  playerA: PlayerId;
  /** Player on the other side, or null for no-man's-land. */
  playerB: PlayerId | null;
  /** Grid keys of tiles on each side (for fog checks). */
  tileKeysA: string[];
  tileKeysB: string[];
  /** Offset sign for playerA's color: -1 = left of polyline, +1 = right. */
  sideSignA: number;
  /** Pre-computed smoothed points with unit normals for fast rendering. */
  renderPoints: { x: number; y: number; nx: number; ny: number }[];
}

export interface FrontLineEvent {
  type: 'breakthrough' | 'shift' | 'encirclement';
  position: { col: number; row: number };
  players: PlayerId[];
}

// ── 1. Influence Map ─────────────────────────────────────────────

const DIRS: [number, number][] = [[-1, 0], [1, 0], [0, -1], [0, 1]];

/**
 * Multi-source Dijkstra flood-fill. Each player's units and owned buildings
 * are sources (cost 0). Tiles are assigned to the player whose source
 * reaches them at lowest cost. Ties broken by lower PlayerId.
 *
 * Returns a flat InfluenceGrid (typed arrays) for zero-GC lookups.
 */
export function computeInfluenceMap(world: TurnWorld): InfluenceGrid {
  const { map } = world;
  const W = map.width;
  const H = map.height;
  const N = W * H;
  const owner = new Int16Array(N).fill(-1);
  const cost = new Float32Array(N).fill(Infinity);

  // Binary min-heap using parallel arrays — no object allocation per node.
  // Pack col+row+owner into a single uint32: col(10)|row(10)|owner(12)
  const packNode = (c: number, r: number, o: number) => (c << 22) | (r << 12) | (o & 0xFFF);
  const unpackCol = (v: number) => v >>> 22;
  const unpackRow = (v: number) => (v >>> 12) & 0x3FF;
  const unpackOwner = (v: number) => v & 0xFFF;

  // Pre-allocate heap arrays (worst case: each tile pushed ~4 times)
  let heapSize = 0;
  const maxHeap = N * 4;
  const heapPacked = new Uint32Array(maxHeap);
  const heapCost = new Float32Array(maxHeap);

  const heapPush = (packed: number, c: number) => {
    let i = heapSize++;
    heapPacked[i] = packed;
    heapCost[i] = c;
    while (i > 0) {
      const p = (i - 1) >> 1;
      if (heapCost[i] < heapCost[p] || (heapCost[i] === heapCost[p] && (heapPacked[i] & 0xFFF) < (heapPacked[p] & 0xFFF))) {
        // swap
        const tmpP = heapPacked[i]; heapPacked[i] = heapPacked[p]; heapPacked[p] = tmpP;
        const tmpC = heapCost[i]; heapCost[i] = heapCost[p]; heapCost[p] = tmpC;
        i = p;
      } else break;
    }
  };

  const heapPop = () => {
    const topPacked = heapPacked[0];
    const topCost = heapCost[0];
    heapSize--;
    if (heapSize > 0) {
      heapPacked[0] = heapPacked[heapSize];
      heapCost[0] = heapCost[heapSize];
      let i = 0;
      for (;;) {
        let s = i;
        const l = 2 * i + 1, r = 2 * i + 2;
        if (l < heapSize && (heapCost[l] < heapCost[s] || (heapCost[l] === heapCost[s] && (heapPacked[l] & 0xFFF) < (heapPacked[s] & 0xFFF)))) s = l;
        if (r < heapSize && (heapCost[r] < heapCost[s] || (heapCost[r] === heapCost[s] && (heapPacked[r] & 0xFFF) < (heapPacked[s] & 0xFFF)))) s = r;
        if (s === i) break;
        const tmpP = heapPacked[i]; heapPacked[i] = heapPacked[s]; heapPacked[s] = tmpP;
        const tmpC = heapCost[i]; heapCost[i] = heapCost[s]; heapCost[s] = tmpC;
        i = s;
      }
    }
    return { packed: topPacked, cost: topCost };
  };

  // Seed sources: all units
  for (const [, unit] of world.units) {
    heapPush(packNode(unit.col, unit.row, unit.owner), 0);
  }
  // Seed sources: all owned buildings
  for (const [, building] of world.buildings) {
    if (building.owner != null) {
      heapPush(packNode(building.col, building.row, building.owner), 0);
    }
  }

  // Dijkstra
  while (heapSize > 0) {
    const cur = heapPop();
    const cc = unpackCol(cur.packed);
    const cr = unpackRow(cur.packed);
    const co = unpackOwner(cur.packed);
    const idx = cr * W + cc;
    const existCost = cost[idx];
    const existOwner = owner[idx];
    if (existOwner >= 0) {
      if (existCost < cur.cost) continue;
      if (existCost === cur.cost && existOwner <= co) continue;
    }
    owner[idx] = co;
    cost[idx] = cur.cost;

    // Expand neighbors
    for (const [dc, dr] of DIRS) {
      const nc = cc + dc;
      const nr = cr + dr;
      if (nc < 0 || nr < 0 || nc >= W || nr >= H) continue;
      const tile = map.tiles[nr][nc];
      const tDef = TERRAIN_DEFS[tile.type];
      const newCost = cur.cost + tDef.movementCost;
      const nIdx = nr * W + nc;
      const nCost = cost[nIdx];
      const nOwner = owner[nIdx];
      if (nOwner >= 0 && (nCost < newCost || (nCost === newCost && nOwner <= co))) continue;
      heapPush(packNode(nc, nr, co), newCost);
    }
  }

  return { width: W, height: H, owner, cost };
}

// ── 2. Contour Extraction ────────────────────────────────────────

interface RawEdge {
  /** Midpoint in grid-space (fractional). */
  gx: number;
  gy: number;
  /** The two tile keys on each side. */
  keyA: string;
  keyB: string;
  playerA: PlayerId;
  playerB: PlayerId | null; // null = no-man's-land
  /** Edge direction for chain-linking: 'h' (horizontal between rows) or 'v' (vertical between cols). */
  dir: 'h' | 'v';
  colA: number; rowA: number;
  colB: number; rowB: number;
}

/**
 * Extract front-line polylines from the influence map.
 * Points are in screen-space (isometric).
 */
export function extractFrontLines(
  grid: InfluenceGrid,
  world: TurnWorld,
  gridToScreen: (col: number, row: number) => { x: number; y: number },
): FrontLineSegment[] {
  const { map } = world;
  const W = grid.width;
  const ownerArr = grid.owner;
  const edges: RawEdge[] = [];

  // Scan horizontal edges (between row r and r+1)
  for (let r = 0; r < map.height - 1; r++) {
    for (let c = 0; c < map.width; c++) {
      const oTop = ownerArr[r * W + c];
      const oBot = ownerArr[(r + 1) * W + c];
      const ownerTop = oTop >= 0 ? oTop : null;
      const ownerBot = oBot >= 0 ? oBot : null;
      if (ownerTop === ownerBot) continue;
      if (ownerTop == null && ownerBot == null) continue;

      edges.push({
        gx: c, gy: r + 0.5,
        keyA: `${c},${r}`, keyB: `${c},${r + 1}`,
        playerA: ownerTop ?? ownerBot!,
        playerB: ownerTop == null ? null : (ownerBot == null ? null : ownerBot),
        dir: 'h',
        colA: c, rowA: r, colB: c, rowB: r + 1,
      });
    }
  }

  // Scan vertical edges (between col c and c+1)
  for (let r = 0; r < map.height; r++) {
    for (let c = 0; c < map.width - 1; c++) {
      const oLeft = ownerArr[r * W + c];
      const oRight = ownerArr[r * W + c + 1];
      const ownerLeft = oLeft >= 0 ? oLeft : null;
      const ownerRight = oRight >= 0 ? oRight : null;
      if (ownerLeft === ownerRight) continue;
      if (ownerLeft == null && ownerRight == null) continue;

      edges.push({
        gx: c + 0.5, gy: r,
        keyA: `${c},${r}`, keyB: `${c + 1},${r}`,
        playerA: ownerLeft ?? ownerRight!,
        playerB: ownerLeft == null ? null : (ownerRight == null ? null : ownerRight),
        dir: 'v',
        colA: c, rowA: r, colB: c + 1, rowB: r,
      });
    }
  }

  // Group edges by player pair (normalized: lower PlayerId first, null last)
  const pairKey = (a: PlayerId, b: PlayerId | null): string => {
    if (b == null) return `${a}:null`;
    return a < b ? `${a}:${b}` : `${b}:${a}`;
  };

  const groups = new Map<string, RawEdge[]>();
  for (const e of edges) {
    const pk = pairKey(e.playerA, e.playerB);
    let arr = groups.get(pk);
    if (!arr) { arr = []; groups.set(pk, arr); }
    arr.push(e);
  }

  // Chain edges into polylines per group (in grid space first)
  interface RawChain {
    edges: RawEdge[];
    playerA: PlayerId;
    playerB: PlayerId | null;
  }
  const rawChains: RawChain[] = [];

  for (const [, groupEdges] of groups) {
    // Build adjacency by grid position
    const byPos = new Map<string, RawEdge>();
    for (const e of groupEdges) byPos.set(`${e.gx},${e.gy}`, e);

    const used = new Set<string>();

    for (const e of groupEdges) {
      const startKey = `${e.gx},${e.gy}`;
      if (used.has(startKey)) continue;

      // Chain from this edge
      const chain: RawEdge[] = [e];
      used.add(startKey);

      // Extend forward (direction-aware)
      let cur = e;
      let prev: RawEdge | null = null;
      for (;;) {
        const next = findNeighborEdge(cur, byPos, used, prev);
        if (!next) break;
        chain.push(next);
        used.add(`${next.gx},${next.gy}`);
        prev = cur;
        cur = next;
      }

      // Extend backward (direction-aware)
      cur = e;
      prev = chain.length > 1 ? chain[1] : null;
      for (;;) {
        const back = findNeighborEdge(cur, byPos, used, prev);
        if (!back) break;
        chain.unshift(back);
        used.add(`${back.gx},${back.gy}`);
        prev = cur;
        cur = back;
      }

      // Normalize player pair: lower PlayerId as playerA for consistent merge/render
      const rawA = chain[0].playerA;
      const rawB = chain[0].playerB;
      const [normA, normB] = rawB != null && rawB < rawA ? [rawB, rawA as PlayerId | null] : [rawA, rawB];
      rawChains.push({
        edges: chain,
        playerA: normA,
        playerB: normB,
      });
    }
  }

  // Post-merge: join chains whose endpoints are within neighbor distance (grid space)
  mergeRawChains(rawChains);

  // Convert to screen-space segments
  const segments: FrontLineSegment[] = [];
  for (const rc of rawChains) {
    let points: { x: number; y: number }[];
    if (rc.edges.length === 1) {
      // Single edge: draw a short segment across the tile boundary
      const ce = rc.edges[0];
      if (ce.dir === 'h') {
        points = [gridToScreen(ce.gx - 0.5, ce.gy), gridToScreen(ce.gx + 0.5, ce.gy)];
      } else {
        points = [gridToScreen(ce.gx, ce.gy - 0.5), gridToScreen(ce.gx, ce.gy + 0.5)];
      }
    } else {
      points = rc.edges.map(ce => gridToScreen(ce.gx, ce.gy));
    }

    // Determine which side of the polyline playerA's territory is on.
    // Use the influence grid to find which spatial side (colA/rowA vs colB/rowB)
    // actually belongs to rc.playerA (chain normalization may have flipped them).
    const firstEdge = rc.edges[0];
    const ownerAtSideA = ownerArr[firstEdge.rowA * W + firstEdge.colA];
    const playerAOnSideA = (ownerAtSideA >= 0 && ownerAtSideA === rc.playerA);
    const refCol = playerAOnSideA ? firstEdge.colA : firstEdge.colB;
    const refRow = playerAOnSideA ? firstEdge.rowA : firstEdge.rowB;
    const playerACenter = gridToScreen(refCol, refRow);
    let sideSignA = -1; // default: playerA on the left
    if (points.length >= 2) {
      const dx = points[1].x - points[0].x;
      const dy = points[1].y - points[0].y;
      const toAx = playerACenter.x - points[0].x;
      const toAy = playerACenter.y - points[0].y;
      const cross = dx * toAy - dy * toAx;
      sideSignA = cross > 0 ? -1 : 1;
    }

    // Pre-compute smoothed points with unit normals for fast rendering
    const smoothed = points.length > 2 ? smoothContour(points) : points;
    const renderPoints: { x: number; y: number; nx: number; ny: number }[] = [];
    for (let i = 0; i < smoothed.length; i++) {
      const { x, y } = smoothed[i];
      let nx = 0, ny = 0;
      if (i < smoothed.length - 1) {
        const dx = smoothed[i + 1].x - x;
        const dy = smoothed[i + 1].y - y;
        const len = Math.sqrt(dx * dx + dy * dy);
        if (len > 0) {
          nx = -dy / len;
          ny = dx / len;
        }
      } else if (renderPoints.length > 0) {
        // Last point: reuse previous normal
        nx = renderPoints[renderPoints.length - 1].nx;
        ny = renderPoints[renderPoints.length - 1].ny;
      }
      renderPoints.push({ x, y, nx, ny });
    }

    segments.push({
      points,
      playerA: rc.playerA,
      playerB: rc.playerB,
      tileKeysA: rc.edges.map(ce => ce.keyA),
      tileKeysB: rc.edges.map(ce => ce.keyB),
      sideSignA,
      renderPoints,
    });
  }

  return segments;
}

const NEIGHBOR_OFFSETS: [number, number][] = [
  // Diagonal (adjacent corner edges): distance ≈ 0.71
  [0.5, 0.5], [-0.5, 0.5], [0.5, -0.5], [-0.5, -0.5],
  // Cardinal (same-type edges side-by-side): distance = 1.0
  [1, 0], [-1, 0], [0, 1], [0, -1],
  // Extended diagonal (e.g. h-edge to v-edge skipping a tile): distance ≈ 1.12
  [0.5, 1], [-0.5, 1], [0.5, -1], [-0.5, -1],
  [1, 0.5], [-1, 0.5], [1, -0.5], [-1, -0.5],
];

// Pre-compute inverse lengths for each offset (constant — no need to recompute per call)
const OFFSET_INV_LEN: number[] = NEIGHBOR_OFFSETS.map(([x, y]) => 1 / Math.sqrt(x * x + y * y));

/**
 * Find an adjacent edge, preferring neighbors that continue the chain direction.
 * When `prev` is provided, neighbors aligned with the prev→from direction are checked first.
 */
function findNeighborEdge(
  from: RawEdge,
  byPos: Map<string, RawEdge>,
  used: Set<string>,
  prev: RawEdge | null,
): RawEdge | null {
  // Build sorted indices if we have a direction to prefer
  let indices: number[];
  if (prev) {
    const dx = from.gx - prev.gx;
    const dy = from.gy - prev.gy;
    const len = Math.sqrt(dx * dx + dy * dy);
    if (len > 0) {
      const nx = dx / len;
      const ny = dy / len;
      // Sort indices by dot product (descending) — precomputed inv lengths
      indices = NEIGHBOR_OFFSETS.map((_, i) => i);
      indices.sort((a, b) => {
        const dotA = (NEIGHBOR_OFFSETS[a][0] * OFFSET_INV_LEN[a]) * nx + (NEIGHBOR_OFFSETS[a][1] * OFFSET_INV_LEN[a]) * ny;
        const dotB = (NEIGHBOR_OFFSETS[b][0] * OFFSET_INV_LEN[b]) * nx + (NEIGHBOR_OFFSETS[b][1] * OFFSET_INV_LEN[b]) * ny;
        return dotB - dotA;
      });
    } else {
      indices = NEIGHBOR_OFFSETS.map((_, i) => i);
    }
  } else {
    indices = NEIGHBOR_OFFSETS.map((_, i) => i);
  }

  for (const idx of indices) {
    const [ox, oy] = NEIGHBOR_OFFSETS[idx];
    const key = `${from.gx + ox},${from.gy + oy}`;
    if (used.has(key)) continue;
    const neighbor = byPos.get(key);
    if (neighbor) return neighbor;
  }
  return null;
}

/** Merge raw chains whose endpoints are within neighbor distance in grid space. */
function mergeRawChains(chains: { edges: RawEdge[]; playerA: PlayerId; playerB: PlayerId | null }[]): void {
  // Max grid distance² for neighbor edges: sqrt(0.5²+0.5²)≈0.71, sqrt(1²+0²)=1.0 → use 1.5²
  // (covers diagonal gaps where edges are sqrt(2) ≈ 1.41 apart)
  const maxDist2 = 1.5 * 1.5;

  let merged = true;
  while (merged) {
    merged = false;
    for (let i = 0; i < chains.length && !merged; i++) {
      const ci = chains[i];
      const iHead = ci.edges[0];
      const iTail = ci.edges[ci.edges.length - 1];

      for (let j = i + 1; j < chains.length && !merged; j++) {
        const cj = chains[j];
        // Only merge chains with the same player pair
        if (!samePair(ci.playerA, ci.playerB, cj.playerA, cj.playerB)) continue;

        const jHead = cj.edges[0];
        const jTail = cj.edges[cj.edges.length - 1];

        if (gridDist2(iTail, jHead) < maxDist2) {
          ci.edges.push(...cj.edges);
          chains.splice(j, 1);
          merged = true;
        } else if (gridDist2(iTail, jTail) < maxDist2) {
          ci.edges.push(...cj.edges.reverse());
          chains.splice(j, 1);
          merged = true;
        } else if (gridDist2(iHead, jTail) < maxDist2) {
          ci.edges.unshift(...cj.edges);
          chains.splice(j, 1);
          merged = true;
        } else if (gridDist2(iHead, jHead) < maxDist2) {
          ci.edges.unshift(...cj.edges.reverse());
          chains.splice(j, 1);
          merged = true;
        }
      }
    }
  }
}

function gridDist2(a: RawEdge, b: RawEdge): number {
  const dx = a.gx - b.gx;
  const dy = a.gy - b.gy;
  return dx * dx + dy * dy;
}

/** Check if two player pairs represent the same boundary (order-independent). */
function samePair(a1: PlayerId, b1: PlayerId | null, a2: PlayerId, b2: PlayerId | null): boolean {
  if (a1 === a2 && b1 === b2) return true;
  if (b1 != null && b2 != null && a1 === b2 && b1 === a2) return true;
  return false;
}

// ── 3. Catmull-Rom Spline ────────────────────────────────────────

/**
 * Smooth a polyline using Catmull-Rom interpolation.
 * Returns a denser array of points.
 */
export function smoothContour(
  points: { x: number; y: number }[],
  _tension = 0.5,
  segmentsPerSpan = 6,
): { x: number; y: number }[] {
  if (points.length <= 2) return [...points];

  const result: { x: number; y: number }[] = [];
  const n = points.length;

  for (let i = 0; i < n - 1; i++) {
    const p0 = points[Math.max(0, i - 1)];
    const p1 = points[i];
    const p2 = points[Math.min(n - 1, i + 1)];
    const p3 = points[Math.min(n - 1, i + 2)];

    for (let s = 0; s < segmentsPerSpan; s++) {
      const t = s / segmentsPerSpan;
      result.push(catmullRomPoint(p0, p1, p2, p3, t));
    }
  }
  // Add final point
  result.push(points[n - 1]);

  return result;
}

/** Standard uniform Catmull-Rom interpolation between p1 and p2. */
function catmullRomPoint(
  p0: { x: number; y: number },
  p1: { x: number; y: number },
  p2: { x: number; y: number },
  p3: { x: number; y: number },
  t: number,
): { x: number; y: number } {
  const t2 = t * t;
  const t3 = t2 * t;

  const x = 0.5 * (
    (2 * p1.x) +
    (-p0.x + p2.x) * t +
    (2 * p0.x - 5 * p1.x + 4 * p2.x - p3.x) * t2 +
    (-p0.x + 3 * p1.x - 3 * p2.x + p3.x) * t3
  );
  const y = 0.5 * (
    (2 * p1.y) +
    (-p0.y + p2.y) * t +
    (2 * p0.y - 5 * p1.y + 4 * p2.y - p3.y) * t2 +
    (-p0.y + 3 * p1.y - 3 * p2.y + p3.y) * t3
  );

  return { x, y };
}

// ── 8. Event Detection ───────────────────────────────────────────

/**
 * Compare two influence maps and detect front-line events.
 * Accepts either legacy Map<string, InfluenceEntry> (for tests) or InfluenceGrid.
 */
export function detectFrontLineEvents(
  prev: Map<string, InfluenceEntry> | InfluenceGrid,
  current: Map<string, InfluenceEntry> | InfluenceGrid,
): FrontLineEvent[] {
  const events: FrontLineEvent[] = [];

  // Normalize inputs to grid-style iteration
  type Flip = { from: PlayerId; to: PlayerId; col: number; row: number };
  const flips: Flip[] = [];

  if ('owner' in current && 'owner' in prev) {
    // Both are InfluenceGrid — fast path
    const g = current;
    const p = prev;
    for (let r = 0; r < g.height; r++) {
      for (let c = 0; c < g.width; c++) {
        const idx = r * g.width + c;
        const co = g.owner[idx];
        const po = p.owner[idx];
        if (po < 0 || co < 0) continue;
        if (po !== co) flips.push({ from: po, to: co, col: c, row: r });
      }
    }
  } else {
    // Legacy Map path (used by tests)
    const curMap = current instanceof Map ? current : influenceGridToMap(current);
    const prevMap = prev instanceof Map ? prev : influenceGridToMap(prev);
    for (const [key, cur] of curMap) {
      const pre = prevMap.get(key);
      if (!pre) continue;
      if (pre.owner !== cur.owner) {
        flips.push({
          from: pre.owner,
          to: cur.owner,
          col: parseInt(key),
          row: parseInt(key.split(',')[1]),
        });
      }
    }
  }

  if (flips.length === 0) return events;

  // Group flips by pair of players
  const pairFlips = new Map<string, Flip[]>();
  for (const flip of flips) {
    const pk = flip.from < flip.to ? `${flip.from}:${flip.to}` : `${flip.to}:${flip.from}`;
    let arr = pairFlips.get(pk);
    if (!arr) { arr = []; pairFlips.set(pk, arr); }
    arr.push(flip);
  }

  for (const [, pairGroup] of pairFlips) {
    if (pairGroup.length >= 5) {
      const cx = Math.round(pairGroup.reduce((s, f) => s + f.col, 0) / pairGroup.length);
      const cy = Math.round(pairGroup.reduce((s, f) => s + f.row, 0) / pairGroup.length);
      events.push({
        type: 'breakthrough',
        position: { col: cx, row: cy },
        players: [pairGroup[0].from, pairGroup[0].to],
      });
    } else if (pairGroup.length > 0) {
      const first = pairGroup[0];
      events.push({
        type: 'shift',
        position: { col: first.col, row: first.row },
        players: [first.from, first.to],
      });
    }
  }

  return events;
}

/** Convert InfluenceGrid to legacy Map (for backward compat / tests). */
export function influenceGridToMap(grid: InfluenceGrid): Map<string, InfluenceEntry> {
  const result = new Map<string, InfluenceEntry>();
  for (let r = 0; r < grid.height; r++) {
    for (let c = 0; c < grid.width; c++) {
      const idx = r * grid.width + c;
      if (grid.owner[idx] >= 0) {
        result.set(`${c},${r}`, { owner: grid.owner[idx], cost: grid.cost[idx] });
      }
    }
  }
  return result;
}
